import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/feature/Navigation';
import LanguageSelector from '../components/common/LanguageSelector';
import CityIntroduction from '../components/feature/CityIntroduction';
import { getCityData, getCitiesByCountry, cityNameToUrlSlug } from '../utils/cityData';

export default function BeijingTime() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['worldclock', 'common']);
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 获取北京城市数据
  const beijingData = getCityData('Beijing');
  
  // 获取中国其他热门城市
  const chineseCities = getCitiesByCountry('CN').slice(0, 20);

  // 用户评价数据
  const [randomizedReviews, setRandomizedReviews] = useState<Array<{
    id: number;
    avatar: string;
    name: string;
    rating: number;
    review: string;
  }>>([]);

  useEffect(() => {
    const userReviews = [
      {
        id: 1,
        avatar: "/images/avatars/Global Business.jpg",
        name: t('user.globalBusiness'),
        rating: 5,
        review: t('reviews.globalBusiness')
      },
      {
        id: 2,
        avatar: "/images/avatars/Remote Developer.jpg",
        name: t('user.remoteTeam'),
        rating: 5,
        review: t('reviews.remoteTeam')
      },
      {
        id: 3,
        avatar: "/images/avatars/Travel Blogger.jpg",
        name: t('user.travelBlogger'),
        rating: 4,
        review: t('reviews.travelBlogger')
      }
    ];

    const shuffled = [...userReviews].sort(() => Math.random() - 0.5);
    setRandomizedReviews(shuffled);
  }, [t, i18n.language]);

  // 定时器更新时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (timezone: string) => {
    try {
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const formatDate = useCallback((timezone: string) => {
    const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';

    try {
      return currentTime.toLocaleDateString(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    } catch (error) {
      return currentTime.toLocaleDateString(locale, {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    }
  }, [currentTime, i18n.language]);

  const getTimeOffset = (timezone: string) => {
    try {
      const offset = getTimezoneOffset(timezone);
      return offset >= 0 ? `UTC+${offset}` : `UTC${offset}`;
    } catch (error) {
      return 'UTC+8';
    }
  };

  const getTimezoneOffset = (timezone: string) => {
    try {
      const now = new Date();
      const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
      const targetTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
      return (targetTime.getTime() - utcTime.getTime()) / (1000 * 60 * 60);
    } catch (error) {
      return 8;
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleFullscreenKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  if (!beijingData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              城市数据加载失败
            </h2>
            <button
              onClick={() => navigate('/worldclock')}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              返回世界时钟
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cityDisplayName = i18n.language.startsWith('zh') ? beijingData.nameZh : beijingData.nameEn;

  // 全屏模式
  if (isFullscreen) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
        onClick={toggleFullscreen}
        onKeyDown={handleFullscreenKeyPress}
        tabIndex={0}
      >
        <div className="text-center" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={toggleFullscreen}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          
          <div className="mb-12">
            <div className="text-[3.75rem] md:text-[8rem] font-mono font-[700] text-blue-400 mb-8">
              {formatTime(beijingData.timezone)}
            </div>
            
            <div className="space-y-4 text-white">
              <div className="text-2xl">
                {formatDate(beijingData.timezone)}
              </div>
              <div className="text-lg text-gray-300">
                {cityDisplayName}: {beijingData.timezone} ({getTimeOffset(beijingData.timezone)})
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back button */}
        <div className="mb-4 md:mb-6">
          <button
            onClick={() => navigate('/worldclock')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            {t('common:button.back')}
          </button>
        </div>

        {/* Current Time Display */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <div className="text-center">
            <div className="flex justify-between items-start mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {cityDisplayName}时间
              </h2>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleFullscreen}
                  className="text-gray-600 hover:text-blue-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex items-center space-x-2"
                  title="全屏显示"
                >
                  <span className="hidden md:block text-sm font-medium">全屏</span>
                  <i className="ri-picture-in-picture-2-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="mb-8 md:mb-10">
              <div className="flex items-center justify-center mb-6 md:mb-8">
                <div className="text-[3.75rem] md:text-8xl font-mono font-bold text-blue-600">
                  {formatTime(beijingData.timezone)}
                </div>
              </div>
              
              <div className="space-y-2 md:space-y-3">
                <div className="text-lg md:text-xl text-gray-600">
                  {formatDate(beijingData.timezone)}
                </div>
                <div className="text-sm md:text-base text-gray-500">
                  {beijingData.timezone} ({getTimeOffset(beijingData.timezone)})
                </div>
                <div className="text-sm text-gray-400">
                  {beijingData.flag} {beijingData.country}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* City Introduction */}
        <CityIntroduction cityName={cityDisplayName} className="mb-6 md:mb-8" />

        {/* Popular Cities in China */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6">
            中国热门城市时间
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chineseCities.map((city) => {
              const isCurrentCity = city.name === beijingData.name || city.nameEn === beijingData.nameEn;
              const displayName = i18n.language.startsWith('zh') ? city.nameZh : city.nameEn;
              
              return (
                <div 
                  key={city.timezone + city.name} 
                  className={`bg-gray-50 rounded-xl p-6 transition-colors ${
                    isCurrentCity 
                      ? 'ring-2 ring-blue-500 bg-blue-50' 
                      : 'hover:bg-gray-100 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{city.flag}</span>
                      <span className="font-semibold text-gray-800 text-lg">{displayName}</span>
                      {isCurrentCity && (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                          当前
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{getTimeOffset(city.timezone)}</span>
                  </div>
                  <div className="text-3xl font-mono font-bold text-gray-900 mb-1">
                    {formatTime(city.timezone)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatDate(city.timezone)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">使用方法</h3>
          <div className="space-y-3 md:space-y-4 text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">1</div>
              <p className="text-sm md:text-base">查看北京实时时间和日期</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">2</div>
              <p className="text-sm md:text-base">时区会自动计算并实时更新</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">3</div>
              <p className="text-sm md:text-base">查看中国其他热门城市时间</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">4</div>
              <p className="text-sm md:text-base">点击全屏按钮可全屏查看时间</p>
            </div>
          </div>
        </div>

        {/* User Reviews */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">用户评价</h3>
          <div className="space-y-4 md:space-y-6">
            {randomizedReviews.slice(0, 3).map((review) => (
              <div key={review.id} className="flex items-start space-x-4">
                <img 
                  src={review.avatar} 
                  alt={review.name}
                  className="w-12 h-12 rounded-full bg-gray-300 flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/avatars/default.png';
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm md:text-base">{review.name}</h4>
                    <div className="flex space-x-0.5">
                      {[...Array(5)].map((_, i) => (
                        <i 
                          key={i} 
                          className={`ri-star${i < review.rating ? '-fill' : '-line'} text-yellow-400 text-sm`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">{review.review}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">关于我们</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                专业的在线时间工具平台，为全球用户提供精确的时间显示、计时器和世界时钟服务。
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">定时器</span> - 精确的倒计时功能
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">秒表</span> - 专业的计时工具
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">世界时钟</span> - 全球城市时间查询
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>北京时间 中国标准时间 CST UTC+8</p>
                <p>世界时钟 时区 国际时间 现在几点</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}